"use client";

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { phCapture } from "@/utils/analytics";
import { shouldIgnoreShortcutTarget } from "./should-ignore-shortcut-target";
import { renderNothingIfChunkFails } from "@/lib/optional-chunk";

const LazyCommandPalette = lazy(() =>
  renderNothingIfChunkFails(import("./CommandPalette").then((module) => module.CommandPalette)).then(
    (CommandPalette) => ({ default: CommandPalette }),
  ),
);

export const OPEN_COMMAND_PALETTE_EVENT = "upsider:open-command-palette";

export function openCommandPalette() {
  window.dispatchEvent(new CustomEvent(OPEN_COMMAND_PALETTE_EVENT));
}

export function CommandPaletteMount() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const openRef = useRef(false);

  const openPalette = useCallback((source: "shortcut" | "rail") => {
    if (openRef.current) return;
    openRef.current = true;
    phCapture("search_opened", { source });
    setMounted(true);
    setOpen(true);
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    openRef.current = nextOpen;
    if (nextOpen) {
      setMounted(true);
    }
    setOpen(nextOpen);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.repeat) return;
      if (typeof event.key !== "string" || event.key.toLowerCase() !== "k") return;
      if (!event.metaKey && !event.ctrlKey) return;
      if (shouldIgnoreShortcutTarget(event.target)) return;

      event.preventDefault();
      openPalette("shortcut");
    };

    const handleExternalOpen = () => openPalette("rail");
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleExternalOpen);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleExternalOpen);
    };
  }, [openPalette]);

  if (!mounted) return null;
  return (
    <Suspense fallback={null}>
      <LazyCommandPalette open={open} onOpenChange={handleOpenChange} />
    </Suspense>
  );
}
